import { Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatButton } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import {
  Account,
  ApplicationLogDetailsRequest,
  ApplicationLogStepRequest,
  ApplicationLogsControllerService,
  ApplicationLogsRequest,
  JointApplicantRequest,
  Step
} from "leia-api";
import { Requestor } from "leia-api/model/requestor";
import { Observable, Subject, combineLatest } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { EnrollmentControllerService } from "skyhopper-api";
import { environment } from "../../environments/environment";
import { AddressVerificationService } from "../core/services/address-verification.service";
import { ReferenceCode } from "../process/models/referenceCode";
import { ReviewService } from "../review/services/review.service";
import { TermsAndDisclosuresService } from "../review/view-disclosures/terms-disclosures.service";
import {
  BASK_BRAND_ID,
  BRANCH_ID,
  CODE_VERIFICATION_PATH,
  CREATE_LOGIN_PATH,
  ERROR_PATH,
  FUNDING_AVAILABLE_KEY,
  FUNDING_PATH,
  HOME_PATH,
  INVITATION_CREATION_PATH,
  INVITATION_CREATION_REVIEW_PATH,
  INVITATION_LOG_KEY,
  INVITATION_PATH,
  JOINT_APPLICATION_DETAILS_KEY,
  JOINT_MAINTENANCE_APPLICATION_PATH,
  JOINT_MAINTENANCE_CREATE_LOGIN_PATH,
  JOINT_MAINTENANCE_PAGE_BREAK_PATH,
  MAINTENANCE_PATH,
  PAGE_BREAK_PATH,
  PROCESSING_PATH,
  REVIEW_PATH,
  SELECT_ACCOUNTS_PATH,
  SUCCESS_PATH,
  TERMS_AND_DISCLOSURES_PATH,
} from "../shared/constants";
import { DeviceType } from "../shared/model/device-type";
import { OnboardingRequest } from "../shared/model/models";
import { SettingsEnum } from "../shared/model/settings.enum";
import { FacadeService } from "../shared/service-facade/facade.service";
import { CodeVerificationService } from "../shared/services/code-verification.service";
import { CoreService } from "../shared/services/core.service";
import {
  EligibilityService,
  EligibilityStatus,
} from "../shared/services/eligibility.service";
import { OnboardingService } from "../shared/services/onboarding.service";
import { SuggestedAddressService } from "../shared/services/suggested-address.service";
import { UtilitiesService } from "../shared/services/utilities.service";
import { ApplicantType } from "../signup/models/applicant-type";
import { SignupService } from "../signup/service/signup.service";
import {
  SectionStates,
  Sections,
} from "../status-tracker/model/section-status";
import { SectionStatusService } from "../status-tracker/section-status.service";
import { CancelDialogComponent } from "./dialogs/cancel-dialog/cancel-dialog.component";
import { DocumentCaptureWarningDialogComponent } from "./dialogs/document-capture-warining-dialog/document-capture-warning-dialog.component";
import { JointInvitationSuccessDialogComponent } from "./dialogs/joint-invitation-success-dialog/joint-invitation-success-dialog.component";
import StateEnum = Step.StateEnum;
import StatusEnum = ApplicationLogsRequest.StatusEnum;
import NameEnum = Step.NameEnum;
import OnboardingStepEnum = ApplicationLogStepRequest.OnboardingStepEnum;
import OnboardingStateEnum = ApplicationLogStepRequest.OnboardingStateEnum;
import OnboardingSourceEnum = ApplicationLogDetailsRequest.OnboardingSourceEnum;
import { NgxPlaidLinkService } from "../plaid/public_api";

//const mParticle = require("@mparticle/web-sdk");

@Component({
  selector: "app-actions",
  templateUrl: "./actions.component.html",
  styleUrls: ["./actions.component.css"],
})
export class ActionsComponent implements OnInit, OnDestroy {
  @Input() isInJointMaintenanceFlow!: boolean;
  @ViewChild("submitButton") submitButton!: MatButton;
  shoppingCartOnboardingRequest: OnboardingRequest;
  applicantsFinishedWithAddressVerification: number = 0;
  applicantsSentThruAddressVerificationMap: Map<number, number> = new Map<
    number,
    number
  >();
  uniqueAddressSet: Set<string> = new Set<string>();
  loading = false;
  isFromBankingCenter = false;

  home_path = `/${HOME_PATH}`;
  processing_path = `/${PROCESSING_PATH}`;
  review_path = `/${REVIEW_PATH}`;
  success_path = `/${SUCCESS_PATH}`;
  error_path = `/${ERROR_PATH}`;
  verify_path = `/${CODE_VERIFICATION_PATH}`;
  maintenance_path = `/${MAINTENANCE_PATH}`;
  page_break_path = `/${PAGE_BREAK_PATH}`;
  joint_account_invitation_path = `/${INVITATION_PATH}`;
  joint_maintenance_page_break = `/${JOINT_MAINTENANCE_PAGE_BREAK_PATH}`;
  joint_account_maintenance_path = `/${JOINT_MAINTENANCE_APPLICATION_PATH}`;
  create_invitation_path = `/${INVITATION_CREATION_PATH}`;
  review_create_invitation_path = `/${INVITATION_CREATION_REVIEW_PATH}`;
  joint_account_create_login_path = `/${JOINT_MAINTENANCE_CREATE_LOGIN_PATH}`;
  create_login_path = `/${CREATE_LOGIN_PATH}`;
  select_accounts_path = `/${SELECT_ACCOUNTS_PATH}`;
  terms_and_disclosures_path = `/${TERMS_AND_DISCLOSURES_PATH}`;
  fund_accounts = `/${FUNDING_PATH}`;

  get isContinueButtonVisible(): boolean {
    const { url } = this.router;
    return (
      url === this.joint_account_create_login_path ||
      url === this.create_login_path
    );
  }

  private _destroySubject$: Subject<void> = new Subject();

  public allFormDataIsValid$: Observable<boolean>;

  public allComponentsInReadonlyState: boolean = true;

  public termsDisclosuresCompleted$!: Observable<boolean>;
  constructor(
    public router: Router,
    public dialog: MatDialog,
    private facadeService: FacadeService,
    private utils: UtilitiesService,
    private coreService: CoreService,
    private eligibilityService: EligibilityService,
    private signupService: SignupService,
    private codeVerificationService: CodeVerificationService,
    private addressVerificationService: AddressVerificationService,
    private suggestedAddressService: SuggestedAddressService,
    private reviewService: ReviewService,
    private enrollmentControllerService: EnrollmentControllerService,
    private onboardingService: OnboardingService,
    private applicationLogsService: ApplicationLogsControllerService,
    private sectionStatusService: SectionStatusService,
    private _termsAndDisclosuresService: TermsAndDisclosuresService,
    private ngxPlaidLinkService: NgxPlaidLinkService
  ) {
    this.allFormDataIsValid$ = combineLatest([
      this.signupService.allFormSectionsComplete$,
      this.reviewService.formInEditMode$,
    ]).pipe(
      map(([allFormsValid, formInEditMode]) => allFormsValid && !formInEditMode)
    );

    this.shoppingCartOnboardingRequest = new OnboardingRequest();
    this.reviewService.editStateMapChanged.subscribe(() => {
      this.allComponentsInReadonlyState =
        this.reviewService.editStateArray.length === 0;
    });
    addressVerificationService.popupAction.subscribe((value: any) => {
      this.loading = value;
    });
    this.termsDisclosuresCompleted$ =
      this._termsAndDisclosuresService.termsDisclosuresCompleted$;

    this.facadeService.stopActionsButtonLoading$.subscribe(val => {
      if(val)
        this.loading = false;
    })
  }

  ngOnInit(): void {
    this.coreService.backButtonClicked.subscribe((next) => {
      this.uniqueAddressSet.clear();
      this.applicantsFinishedWithAddressVerification = 0;
    });

    if (this.facadeService.isFromBranch()) {
      if (!environment.production)
        console.warn(
          "Customer is from branch: " +
            this.facadeService.getLocalStorageItem(BRANCH_ID)
        );
      this.isFromBankingCenter = true;
    }

    // Subscribe to know when the Address Verification has completed. If we have multiple applicants we will move to the next.
    this.addressVerificationService.addressVerificationComplete
      .pipe(takeUntil(this._destroySubject$))
      .subscribe(() => {
        if (this.suggestedAddressService.isCloseButtonActivated) {
          return;
        }
        this.applicantsFinishedWithAddressVerification++;

        if (
          this.applicantsFinishedWithAddressVerification ===
          this.uniqueAddressSet.size
        ) {
          if (this.isFromBankingCenter) {
            this.generateSecureAttributes();
            return;
          }

          this.loading = false;
          this.sectionStatusService.updateStatusTrackerSectionStatus(
            Sections.YourApplication,
            SectionStates.complete
          );
          this.coreService.navigate(`/${REVIEW_PATH}`);
          this.utils.scrollToElement("welcome");
        } else {
          let nextApplicantId: number = 0;

          this.shoppingCartOnboardingRequest.applicants.forEach((value) => {
            if (
              nextApplicantId === 0 &&
              !this.applicantsSentThruAddressVerificationMap.has(
                value.applicantId
              )
            ) {
              nextApplicantId = value.applicantId;
            }
          });

          if (nextApplicantId > 0) {
            this.triggerAddressVerificationDialog(nextApplicantId);
          }
        }
      });
  }

  ngOnDestroy(): void {
    this._destroySubject$.next();
    this._destroySubject$.complete();
  }

  triggerAddressVerificationDialog(applicantId: number) {
    this.applicantsSentThruAddressVerificationMap.set(applicantId, applicantId);
    this.addressVerificationService.verifyAddress(applicantId);
  }

  onCancel(): void {
    const dialogRef = this.dialog.open(CancelDialogComponent, {
      width: "410px",
    });

    dialogRef.afterClosed().subscribe(async (userRequestsCancel) => {
      if (!userRequestsCancel) return;

      let log = this.utils.createLog("BB8", "Application canceled by user");
      let applicationLogDetailRequest =
        this.utils.createApplicationLogDetailsRequest(
          OnboardingSourceEnum.Bb8,
          "Application canceled by user"
        );

      let step = this.utils.createStep(
        NameEnum.CancelApplication,
        StateEnum.Success,
        [log]
      );
      let applicationLogStepRequest =
        this.utils.createApplicationLogStepRequest(
          OnboardingStepEnum.CancelApplication,
          OnboardingStateEnum.Success,
          OnboardingSourceEnum.Bb8,
          [applicationLogDetailRequest]
        );

      let { url } = this.router;

      let status = StatusEnum.Canceled;

      if (url.includes(this.create_login_path)) {
        let applicationRequest =
          this.facadeService.getShoppingCartApplicationLogsRequest();

        applicationRequest.rcCode =
          ReferenceCode.OnlineBankingEnrollmentFailure;
        applicationLogStepRequest.rcCode =
          ReferenceCode.OnlineBankingEnrollmentFailure;

        applicationRequest.applicationLogStepRequest =
          applicationLogStepRequest;

        this.facadeService.setShoppingCartApplicationLogsRequest(
          applicationRequest
        );
        status = StatusEnum.Approved;
      }

      await this.updateApplication(step, applicationLogStepRequest, status);

      let cancelUrl = "";

      if (this.facadeService.isFromBranch()) {
        cancelUrl = this.facadeService.getStorageItem(
          SettingsEnum.BranchCancelUrl
        );
      } else {
        cancelUrl = this.facadeService.getStorageItem(SettingsEnum.CancelUrl);
      }

      window.location.href = cancelUrl;

      // clear storage when leaving the app
      this.facadeService.clearStorage();
      this.facadeService.clearAllCookies();
    });
  }

  generateSecureAttributes() {
    this.codeVerificationService
      .generateSecureAttributes(this.facadeService.getStorageItem("SessionId"))
      .toPromise()
      .then((secureSessionId) => {
        this.loading = false;
        this.coreService.navigate(
          `/${CODE_VERIFICATION_PATH}/${secureSessionId}`
        );
      })
      .catch((error) => {
        this.loading = false;
        console.warn(error);
      });
  }

  async onProceedToReview() {
    this.loading = true;
    this.applicantsSentThruAddressVerificationMap = new Map<number, number>();
    this.suggestedAddressService.isCloseButtonActivated = false;
    const brandId = +this.facadeService.getStorageItem("brandId");
    //comment mParticle, CDP scripts are live, remove the code later
    // if (brandId != BASK_BRAND_ID) {
    //   mParticle.default.logPageView("Step 2 - Verify", {
    //     page: window.location.toString(),
    //   });
    // }

    const response =
      await this.facadeService.getCurrentProductSelectionEligibility();

    if (response && response.status === EligibilityStatus.Failure) {
      return;
    }

    this.shoppingCartOnboardingRequest =
      this.facadeService.getShoppingCartOnboardingRequest();

    this.shoppingCartOnboardingRequest.applicants.forEach((value) => {
      this.uniqueAddressSet.add(value.tellUsAboutYourself.addressLine1);
    });

    let nextApplicantId: number = 0;
    this.shoppingCartOnboardingRequest.applicants.forEach((value) => {
      // For the first time through - use the Primary Applicant
      if (value.applicantType === ApplicantType.Primary) {
        if (
          !this.applicantsSentThruAddressVerificationMap.has(value.applicantId)
        )
          nextApplicantId = value.applicantId;
      }
    });

    if (
      nextApplicantId > 0 ||
      !this.suggestedAddressService.isCloseButtonActivated
    )
      this.triggerAddressVerificationDialog(nextApplicantId);
    else {
      this.loading = false;
      this.coreService.navigate(`/${REVIEW_PATH}`);
      window.scrollTo(0, 0);
    }
  }

  onCreateLogin() {
    this.loading = true;
    this.shoppingCartOnboardingRequest =
      this.facadeService.getShoppingCartOnboardingRequest();

    const { userName, password } =
      this.shoppingCartOnboardingRequest.customerLogin;
    let olbEnrollmentRequest = JSON.parse(
      this.facadeService.getStorageItem("olb-enrollment-request")
    );

    olbEnrollmentRequest.primaryApplicant.userName = userName;
    olbEnrollmentRequest.primaryApplicant.pwd = password;

    this.enrollmentControllerService
      .createOlbCredentials(olbEnrollmentRequest)
      .toPromise()
      .then(async (response) => {
        if (response.isSuccess) {
          let log = this.utils.createLog(
            "BB8",
            "Create OLB Credentials has successful."
          );

          let applicationLogDetailsRequest =
          this.utils.createApplicationLogDetailsRequest(
              OnboardingSourceEnum.Bb8,
              "Create OLB Credentials has successful."
            );

          let additionalLogDetailsResponse =
          this.utils.createApplicationLogDetailsRequest(
              OnboardingSourceEnum.Bb8,
              'Response: '+JSON.stringify(response)
            );

            let step = this.utils.createStep(
            NameEnum.CaliperUserEnrollment,
            StateEnum.Success,
            [log]
          );

          let applicationLogStepRequest =
            this.utils.createApplicationLogStepRequest(
              OnboardingStepEnum.CaliperUserEnrollment,
              OnboardingStateEnum.Success,
              OnboardingSourceEnum.Bb8,
              [applicationLogDetailsRequest, additionalLogDetailsResponse]
            );

            await this.updateApplication(
            step,
            applicationLogStepRequest,
            StatusEnum.Approved
          );

          this.onboardingService.setOlbAccountCreationSuccessfull();
        } else {
          await this.handleOnCreateLoginExceptionOrError(response, 'Create OLB Credentials has failed.');
        }
        this.loading = false;

        if(this.facadeService.getStorageItemAsBoolean(FUNDING_AVAILABLE_KEY)){
          this.coreService.navigate(`/${FUNDING_PATH}`);
        }
        else{
          this.coreService.navigate(`/${SUCCESS_PATH}`);
        }
      })
      .catch(async (error) => {
        await this.handleOnCreateLoginExceptionOrError(error, 'Exception occurred while creating OLB Credentials.');
        this.loading = false;
        this.coreService.navigate(`/${SUCCESS_PATH}`);
      });
  }

  async onProceed(): Promise<void> {
    this.signupService.reviewSubmissionStatusChange.emit(true);
    this.loading = true;

    await this.facadeService.updateApplicationOnSubmit(
      this.facadeService.getSessionId()
    );

    let validFormLog = this.utils.createLog(
      "BB8",
      "Validated user-provided fields. Checking eligibility."
    );
    let validFormLog_ApplicationLogDetailRequest =
      this.utils.createApplicationLogDetailsRequest(
        OnboardingSourceEnum.Bb8,
        "Validated user-provided fields. Checking eligibility."
      );

    await this.eligibilityService.updateApplicationEligibilityFields();

    this.shoppingCartOnboardingRequest =
      this.facadeService.getShoppingCartOnboardingRequest();

    let eligibleLog = this.utils.createLog(
      "BB8",
      "All applicants are eligible."
    );
    let applicationLogDetailRequest =
      this.utils.createApplicationLogDetailsRequest(
        OnboardingSourceEnum.Bb8,
        "All applicants are eligible."
      );

    let step = this.utils.createStep(
      NameEnum.EligibilityReview2,
      StateEnum.Success,
      [validFormLog, eligibleLog]
    );
    let applicationLogStepRequest = this.utils.createApplicationLogStepRequest(
      OnboardingStepEnum.EligibilityReview2,
      OnboardingStateEnum.Success,
      OnboardingSourceEnum.Bb8,
      [validFormLog_ApplicationLogDetailRequest, applicationLogDetailRequest]
    );

    await this.updateApplication(step, applicationLogStepRequest);

    let anyApplicantNeedsKyc: boolean = false;

    this.facadeService
      .getShoppingCartApplicationLogsRequest()
      .applicants?.forEach((value) => {
        if (value.isKyc) anyApplicantNeedsKyc = true;
      });

    if (!anyApplicantNeedsKyc) {
      this.loading = false;
      this.sectionStatusService.updateStatusTrackerSectionStatus(
        Sections.TermsAndDisclosure,
        SectionStates.complete
      );

      this.coreService.navigate(`/${PROCESSING_PATH}`);
    } else {
      this.dialog
        .open(DocumentCaptureWarningDialogComponent, {
          width: "400px",
          autoFocus: false,
        })
        .afterClosed()
        .subscribe((value) => {
          this.loading = false;
          if (value) {
            this.sectionStatusService.updateStatusTrackerSectionStatus(
              Sections.TermsAndDisclosure,
              SectionStates.complete
            );
            this.coreService.navigate(`/${PROCESSING_PATH}`);
          }
        });
    }
  }

  updateApplication(
    step: Step,
    applicationLogStepRequest: ApplicationLogStepRequest,
    status: StatusEnum = StatusEnum.Started
  ): Promise<void> {
    let applicationRequest =
      this.facadeService.getShoppingCartApplicationLogsRequest();

    applicationRequest.step = step;
    applicationRequest.applicationLogStepRequest = applicationLogStepRequest;
    applicationRequest.status = status;

    this.facadeService.setShoppingCartApplicationLogsRequest(
      applicationRequest
    );
    return this.facadeService.updateApplication();
  }

  isCancelButtonVisible(): boolean {
    const { url } = this.router;
    return (
      url !== this.processing_path &&
      url !== this.success_path &&
      url !== this.error_path &&
      url !== this.maintenance_path &&
      url !== this.page_break_path &&
      url !== this.joint_maintenance_page_break &&
      !url.includes(this.verify_path) &&
      !url.includes(this.joint_account_invitation_path) &&
      !this.facadeService.isInAuthenticatedFlow$.value[0]
    );
  }

  isProceedButtonVisible(): boolean {
    return (
      this.router.url.indexOf(this.review_path) > -1 ||
      this.router.url.indexOf(this.terms_and_disclosures_path) > -1
    );
  }

  onProceedToReviewJointMaintenanceInvitation() {
    this.coreService.navigate(`/${INVITATION_CREATION_REVIEW_PATH}`);
    window.scrollTo(0, 0);
  }

  onCreateJointMaintenanceInvitation() {
    this.loading = true;
    const { firstName, lastName, birthDate, emailAddress } = JSON.parse(
      this.facadeService.getStorageItem(INVITATION_LOG_KEY)
    );

    const newAccountInfo = this.facadeService.getNewAccountInformation();
    const applicationLog =
      this.facadeService.getShoppingCartApplicationLogsRequest();
    const person = applicationLog.applicants![0];

    this.facadeService
      .getProduct(applicationLog.products![0].productUID!)
      .then((product) => {
        const account: Account = {
          accountType: product.accountType,
          accountNumber: newAccountInfo.accountNumber,
          productCode: String(product.code),
        };

        let accounts: Array<Account> = [];

        accounts.push(account);

        const invitedBy: Requestor = {
          fullName: person.firstName + " " + person.lastName,
          customerNumber: person.cif,
          email: person.email,
        };

        const jointInvitationRequest: JointApplicantRequest = {
          firstName: firstName,
          lastName: lastName,
          dateOfBirth: UtilitiesService.removeTimeFromDateString(birthDate),
          email: emailAddress,
          accounts: accounts,
          invitedBy: invitedBy,
          parentApplicationId: applicationLog.sessionId,
        };

        this.facadeService
          .createJointMaintenanceInvitation(jointInvitationRequest)
          .toPromise()
          .then((sessionId) => {
            this.facadeService
              .getExpiryDateFromSessionId(sessionId)
              .toPromise()
              .then((expiryDate) => {
                this.facadeService.addStorageItem(
                  JOINT_APPLICATION_DETAILS_KEY,
                  expiryDate
                );
                this.dialog.open(JointInvitationSuccessDialogComponent, {
                  panelClass: "popup-dialog",
                  width: "100%",
                  maxWidth: "680px",
                  height: "100%",
                  maxHeight: "400px",
                  autoFocus: false,
                  disableClose: true,
                });
                document.body.classList.add("cdk-global-scrollblock");
                this.loading = false;
              });
          });
      });
  }

  async onClickOfContinue() {
    switch (this.router.url) {
      case this.create_invitation_path:
        this.onProceedToReviewJointMaintenanceInvitation();
        break;
      case this.joint_account_create_login_path:
      case this.create_login_path:
        this.onCreateLogin();
        break;
      case this.select_accounts_path:
        this.sectionStatusService.updateStatusTrackerSectionStatus(
          Sections.SelectAccounts,
          SectionStates.complete
        );
        await this.facadeService.addProductSelectionToApplication();
        this.coreService.navigate(`/${HOME_PATH}`);
        break;
      case this.fund_accounts:
        this.onFundAccountsNow();
        break;
      default:
        this.onProceedToReview();
    }
  }

  async onClickOfProceed() {
    if (this.router.url === this.review_create_invitation_path) {
      this.onCreateJointMaintenanceInvitation();
    } else if (this.router.url === this.review_path) {
      this.sectionStatusService.updateStatusTrackerSectionStatus(
        Sections.ReviewApplication,
        SectionStates.complete
      );
      await this.handleProductEligibility();
      this.coreService.navigate(`/${TERMS_AND_DISCLOSURES_PATH}`);
    } else if (this.router.url === this.terms_and_disclosures_path) {
      this.loading = true;
        this.eligibilityService.getCanProceedObservable()
        .pipe(takeUntil(this._destroySubject$),
      )
        .subscribe((canProceed) => {
          if (canProceed) {
            this.signupService.termsAccepted.next(true);
            this.signupService.allTermsConditionsAccepted = true;
            this.loading = false;
            this.onProceed();
          }
        });
    }  else {
      await this.handleProductEligibility();

      let panelClass = "";
      const currentDeviceType = this.facadeService.getDeviceType();
      if (
        currentDeviceType === DeviceType.MOBILE ||
        currentDeviceType === DeviceType.TABLET
      ) {
        panelClass = "mobile-dialog";
      }
    }
  }

  async handleProductEligibility() {
    this.loading = true;
    const productEligibility =
      await this.facadeService.getCurrentProductSelectionEligibility();

    //check if response is empty {} or product key is empty [] & throw RC340
    if (
      Object.keys(productEligibility).length === 0 ||
      (!productEligibility.rcCode && !productEligibility.products?.length)
    ) {
      this.handleEligibilityError(ReferenceCode.InternalFailure);
      return;
    }

    if (
      productEligibility &&
      productEligibility.status === EligibilityStatus.Failure
    ) {
      return;
    }

    this.loading = false;
  }

  /**
   * Determines if the button should be disabled based on form validity and terms completion.
   */
  getButtonDisabledState(): Observable<boolean> {
    return combineLatest([
      this.allFormDataIsValid$,
      this.termsDisclosuresCompleted$,
    ]).pipe(
      map(([allFormDataIsValid, termsDisclosuresCompleted]) => {
        if (this.router.url === this.fund_accounts) {
          return this.loading || !this.facadeService.getIsFundingAccountRadioTouched();
        }
        if (this.router.url === this.terms_and_disclosures_path) {
          return (
            this.loading || !allFormDataIsValid || !termsDisclosuresCompleted
          );
        }
        return this.loading || !allFormDataIsValid;
      }),
      takeUntil(this._destroySubject$)
    );
  }

  onFundAccountsNow() {
    if(this.facadeService.fundAccountNow()) {
      this.ngxPlaidLinkService.openPlaidPopup();
      this.loading = true;
    } else {
      this.coreService.navigate(`/${SUCCESS_PATH}`);
    }
  }

  handleEligibilityError(rcCode: string) {
    this.facadeService
      .processOnboardingError(rcCode as ReferenceCode)
      .subscribe(() => {
        this.coreService.navigate(`/${ERROR_PATH}`);
      });
    return false;
  }

  async handleOnCreateLoginExceptionOrError(resOrErrorData: unknown, message: string) {
    let log = this.utils.createLog(
            "BB8",
            message
          );
          let applicationLogDetailsRequest =
            this.utils.createApplicationLogDetailsRequest(
              OnboardingSourceEnum.Bb8,
              message
            );
          let additionalLogDetailsStep =
            this.utils.createApplicationLogDetailsRequest(
              OnboardingSourceEnum.Bb8,
              `Encountered ${ReferenceCode.OnlineBankingEnrollmentFailure.toString()}`
            );
          let additionalLogDetailsResponse =
          this.utils.createApplicationLogDetailsRequest(
              OnboardingSourceEnum.Bb8,
              'ErrorOrResponseData: '+JSON.stringify(resOrErrorData)
            );

          let step = this.utils.createStep(
            NameEnum.CaliperUserEnrollment,
            StateEnum.Fail,
            [log]
          );
          let applicationLogStepRequest =
            this.utils.createApplicationLogStepRequest(
              OnboardingStepEnum.CaliperUserEnrollment,
              OnboardingStateEnum.Fail,
              OnboardingSourceEnum.Bb8,
              [applicationLogDetailsRequest, additionalLogDetailsStep, additionalLogDetailsResponse]
            );

          let applicationRequest =
            this.facadeService.getShoppingCartApplicationLogsRequest();

          applicationRequest.rcCode =
            ReferenceCode.OnlineBankingEnrollmentFailure;
          applicationLogStepRequest.rcCode =
            ReferenceCode.OnlineBankingEnrollmentFailure;

          applicationRequest.applicationLogStepRequest =
            applicationLogStepRequest;

          this.facadeService.setShoppingCartApplicationLogsRequest(
            applicationRequest
          );
          await this.updateApplication(
            step,
            applicationLogStepRequest,
            StatusEnum.Approved
          );
  }

}
